import React, { useMemo } from 'react';
import {
  Grid,
  Button,
  Typography,
  AlertColor,
  DialogContent,
  DialogActions
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { GroupTypeDetails } from 'graphql/types';
import { useScrollbarStyles } from 'modules/common';
import { useFeatureFlag } from 'modules/common/hooks/useFeatureFlag';
import { SimpleDropDown } from './SimpleDropDown';
import { EnabledDisabled } from './constant';
import { GroupSelectionRow } from './GroupSelectionRow';
import { ToastSnackBar } from './ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: ({
      isTiAutoAcceptUserSettingsEnabled
    }: {
      isTiAutoAcceptUserSettingsEnabled: boolean;
    }) =>
      isTiAutoAcceptUserSettingsEnabled ? theme.spacing(0) : theme.spacing(2.5),
    alignContent: 'flex-start',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  contentDialog: {
    padding: theme.spacing(0, 1, 4, 0)
  },
  actions: {
    paddingBottom: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  content: {
    display: 'flex',
    borderRadius: theme.spacing(0, 1.25, 0, 0),
    alignContent: 'flex-start'
  },
  dropDown: {
    marginRight: theme.spacing(1)
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  button: {
    marginLeft: theme.spacing(2),
    textTransform: 'none'
  },
  buttonText: {
    fontWeight: theme.typography.fontWeightMedium
  },
  submitButton: {
    display: 'flex',
    alignContent: 'flex-start',
    marginLeft: theme.spacing(2),

    marginTop: theme.spacing(2),

    padding: theme.spacing(1, 4)
  },
  submitSettingDialog: {
    display: 'flex',
    alignContent: 'flex-start',
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0),
    padding: theme.spacing(0.75, 2)
  }
}));
type SettingBodyProps = {
  enabledGroupTypes: GroupTypeDetails[];
  fields: Record<string, any>[];
  handleRemove: (index?: number | number[] | undefined) => void;
  handleClick: () => void;
  show: boolean;
  category: AlertColor | undefined;
  message: string | undefined;
  isDirty: boolean;
  saving: boolean;
  fieldName: string;
  defaultRowValue: EnabledDisabled | undefined;
  onClose?: () => void;
};

export const SettingBody: React.FC<SettingBodyProps> = ({
  enabledGroupTypes,
  fields,
  handleRemove,
  handleClick,
  show,
  category,
  message,
  isDirty,
  saving,
  fieldName,
  defaultRowValue,
  onClose
}: SettingBodyProps) => {
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const scrollBarStyles = useScrollbarStyles();
  const classes = useStyles({ isTiAutoAcceptUserSettingsEnabled });
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    return [
      {
        key: EnabledDisabled.enabled,
        value: formatMessage({ id: 'settings.enabledDisabled.enabled' })
      },
      {
        key: EnabledDisabled.disabled,
        value: formatMessage({ id: 'settings.enabledDisabled.disabled' })
      }
    ];
  }, [formatMessage]);

  const bodyContent = useMemo(() => {
    return (
      <div>
        {fields.map((field, index) => (
          <GroupSelectionRow
            key={field.id}
            options={options}
            enabledGroupTypes={enabledGroupTypes}
            rowID={`${fieldName}.${index}`}
            rowIndex={index}
            field={field}
            onRemove={handleRemove}
          />
        ))}

        <div className={classes.content}>
          <div className={classes.dropDown}>
            <SimpleDropDown
              value={defaultRowValue}
              options={options}
              rowID={'defaultRow'}
            />
          </div>
          <Typography display="inline" className={classes.text} variant="body2">
            <FormattedMessage id="settings.defaultValue" />
          </Typography>
          <Button
            className={classes.button}
            size="small"
            color="primary"
            onClick={handleClick}
            data-testid={'add-target-button'}
            disabled={!enabledGroupTypes || enabledGroupTypes!.length === 0}
          >
            <AddOutlinedIcon fontSize="inherit" />
            <Typography
              display="inline"
              className={classes.buttonText}
              variant="body2"
            >
              <FormattedMessage id="settings.addTargetButton" />
            </Typography>
          </Button>
        </div>
      </div>
    );
  }, [
    classes,
    defaultRowValue,
    enabledGroupTypes,
    fieldName,
    fields,
    handleClick,
    handleRemove,
    options
  ]);

  const footerContent = useMemo(() => {
    return (
      <>
        {isTiAutoAcceptUserSettingsEnabled && (
          <Button
            variant="text"
            onClick={onClose}
            data-testid="settings-cancel-button"
          >
            <Typography color="textSecondary" fontWeight={500}>
              <FormattedMessage id="settingsDialog.cancel" />
            </Typography>
          </Button>
        )}
        <Button
          className={
            isTiAutoAcceptUserSettingsEnabled
              ? classes.submitSettingDialog
              : classes.submitButton
          }
          disabled={saving || !isDirty}
          size={isTiAutoAcceptUserSettingsEnabled ? 'medium' : 'large'}
          color="primary"
          variant="contained"
          data-testid="save-settings-button"
          type="submit"
        >
          <FormattedMessage id="settings.save" />
        </Button>
      </>
    );
  }, [
    isTiAutoAcceptUserSettingsEnabled,
    onClose,
    classes.submitSettingDialog,
    classes.submitButton,
    saving,
    isDirty
  ]);

  return (
    <Grid
      className={classes.container}
      data-testid="setting-content-page-start-end"
    >
      {isTiAutoAcceptUserSettingsEnabled ? (
        <DialogContent
          className={classNames(
            classes.contentDialog,
            scrollBarStyles.scrollbar
          )}
        >
          {bodyContent}
        </DialogContent>
      ) : (
        bodyContent
      )}
      {isTiAutoAcceptUserSettingsEnabled ? (
        <DialogActions className={classes.actions}>
          {footerContent}
        </DialogActions>
      ) : (
        <div> {footerContent}</div>
      )}
      {!isTiAutoAcceptUserSettingsEnabled && (
        <ToastSnackBar show={show} category={category} message={message} />
      )}
    </Grid>
  );
};
